import { useEffect, useState } from "react";
import { GraduationCap, BarChart3, Users, BookOpen, FileText, LineChart, MessageSquare } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const iconMap = {
  GraduationCap: GraduationCap,
  BarChart3: BarChart3,
  Users: Users,
  BookOpen: BookOpen,
  FileText: FileText,
  LineChart: LineChart,
  MessageSquare: MessageSquare
};

const ServicesPage = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services`);
      setServices(response.data);
    } catch (error) {
      console.error("Error fetching services:", error);
    } finally {
      setLoading(false);
    }
  };

  const colorClasses = [
    { bg: 'bg-primary', text: 'text-primary', border: 'border-primary' },
    { bg: 'bg-accent-sage', text: 'text-accent-sage', border: 'border-accent-sage' },
    { bg: 'bg-accent-sand', text: 'text-accent-sand', border: 'border-accent-sand' },
    { bg: 'bg-accent-clay', text: 'text-accent-clay', border: 'border-accent-clay' },
  ];

  return (
    <div className="flex-1 bg-[#FAF8F3]" data-testid="services-page">
      {/* Header */}
      <section className="bg-primary-dark text-white py-16 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(255,255,255,.1) 35px, rgba(255,255,255,.1) 70px)'}}></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <h1 
            className="text-4xl sm:text-5xl font-bold mb-4 font-heading"
            data-testid="services-heading"
          >
            Our Services
          </h1>
          <p className="text-lg text-gray-200 max-w-3xl">
            Comprehensive population-health, community-health, and public-health solutions designed to strengthen outcomes and build capacity.
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-12">
              <p className="text-neutral-gray">Loading services...</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-8" data-testid="services-list">
              {services.map((service, index) => {
                const IconComponent = iconMap[service.icon] || Users;
                const colors = colorClasses[index % colorClasses.length];
                return (
                  <Card key={service.id} className="hover:shadow-xl transition-all duration-300 bg-white border-l-4 hover:-translate-y-1" style={{borderLeftColor: `var(--${colors.bg})`}} data-testid={`service-card-${service.id}`}>
                    <CardHeader>
                      <div className="flex items-start gap-4">
                        <div className={`w-14 h-14 ${colors.bg} rounded-2xl flex items-center justify-center flex-shrink-0 shadow-md`}>
                          <IconComponent className="text-white" size={28} />
                        </div>
                        <div className="flex-1">
                          <CardTitle className={`text-2xl mb-2 font-heading ${colors.text}`}>
                            {service.title}
                          </CardTitle>
                          <div className="flex gap-2 flex-wrap">
                            <Badge variant="secondary" className="text-xs">{service.category}</Badge>
                            {service.duration && (
                              <Badge variant="outline" className="text-xs">{service.duration}</Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-neutral-charcoal mb-4 leading-relaxed">{service.description}</p>
                      <div className="space-y-2">
                        <h4 className="font-semibold text-sm text-neutral-charcoal font-heading">Key Features:</h4>
                        <ul className="space-y-2">
                          {service.details.map((detail, idx) => (
                            <li key={idx} className="flex items-start gap-2 text-sm text-neutral-gray">
                              <span className={`${colors.text} mt-1 font-bold`}>•</span>
                              <span>{detail}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4 font-heading text-neutral-charcoal">
            Interested in Our Services?
          </h2>
          <p className="text-lg text-neutral-gray mb-8">
            Contact us to learn more about how we can support your community health initiatives.
          </p>
          <a href="/contact">
            <button 
              className="px-8 py-3 bg-primary text-white rounded-md font-semibold hover:bg-primary-dark transition-colors shadow-lg"
              data-testid="services-cta-button"
            >
              Get Started
            </button>
          </a>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
